/* eslint-disable max-lines-per-function */
import React from "react";
import { useMutation } from "@apollo/client";
import { Form, Formik } from "formik";
import { DialogActions, IconButton, Tooltip, Typography } from "@material-ui/core";
import { ArrowBack, ArrowForward } from "@material-ui/icons";

import { DialogContent, DialogTitle } from "components/haven-design/modal/dialog";

import {
  RemoveApplicantFromList,
  RemoveApplicantFromListVariables,
  RemoveApplicantFromListWithActivity,
  RemoveApplicantFromListWithActivityVariables,
  SaveAsManuallyContactedWithActivity,
  SaveAsManuallyContactedWithActivityVariables,
  SaveAsManuallyContactedWithoutActivity,
  SaveAsManuallyContactedWithoutActivityVariables,
  SaveManualContactActivity,
  SaveManualContactActivityVariables,
  FetchManualContactsView_interestList_scheduledUpdate_manualContacts_results as RequestedResult,
} from "types/gql-types";
import { useSnackbar } from "components/haven-design/snackbar";
import { Modal } from "components/haven-design";
import { Button, DropDownButton } from "components/haven-design/buttons";
import { RadioCardGroup } from "components/haven-design/form/radio/radio-card";
import { MaterialFormikSelect as SelectWithLabel } from "components/haven-design/form/select";
import { InputWithErrorLabel as Input } from "components/haven-design/form/inputs";
import { FormikStructuredReasons } from "components/structured-reasons";
import {
  MUTATION_MANUALLY_CONTACTED_WITH_ACTIVITY,
  MUTATION_MANUALLY_CONTACTED_WITHOUT_ACTIVITY,
  MUTATION_REMOVE_FROM_WAITLIST_WITH_ACTIVITY,
  MUTATION_REMOVE_FROM_WAITLIST_WITHOUT_ACTIVITY,
  MUTATION_SAVE_ACTIVITY,
} from "../query";
import styles from "./index.module.css";
import PhoneNumber from "./phone-number";
import UpdateFormFields from "./update-form-fields";
import submitFunction, { buildApplicantReference, manualContactValidationSchema, DISPOSITION_MESSAGE, Disposition } from "./helpers";

interface ApplicantData {
  address1: string;
  address2: string;
  city: string;
  email: string;
  householdCount: number;
  householdNetIncome: number;
  primaryPhone: string;
  state: string;
  zip: string;
}

interface FormValues {
  applicant: Partial<ApplicantData>;
  disposition: string;
  notes: string;
  removalReason: string;
  result: null | "update" | "remove";
}

const initialFormValues: FormValues = {
  applicant: {},
  disposition: DISPOSITION_MESSAGE[Disposition.NoLog],
  notes: "",
  removalReason: "No response",
  result: null,
};

const DISPOSITIONS = Object.entries(DISPOSITION_MESSAGE)
  .map(([, value]) => ({ name: value, value }));

interface Props {
  applicants: RequestedResult[]
  viewing: RequestedResult | null;
  setViewing: (input: RequestedResult | null) => void;
}

const ModalForm: React.FC<Props> = ({ applicants, viewing, setViewing }) => {
  const clearViewing = () => setViewing(null);
  const [submitBtnIndex, setSubmitBtnIndex] = React.useState(0);
  const [nextApplicant, setNextApplicant] = React.useState<string | null>(null);

  const { openSnackbar } = useSnackbar();
  const [submitAttempt] = useMutation<SaveManualContactActivity,
  SaveManualContactActivityVariables>(MUTATION_SAVE_ACTIVITY);
  const [submitUpdateWithActivity] = useMutation<SaveAsManuallyContactedWithActivity,
  SaveAsManuallyContactedWithActivityVariables>(MUTATION_MANUALLY_CONTACTED_WITH_ACTIVITY);
  const [submitUpdateWithoutActivity] = useMutation<SaveAsManuallyContactedWithoutActivity,
  SaveAsManuallyContactedWithoutActivityVariables>(MUTATION_MANUALLY_CONTACTED_WITHOUT_ACTIVITY);
  const [submitRemoveWithActivity] = useMutation<RemoveApplicantFromListWithActivity,
  RemoveApplicantFromListWithActivityVariables>(MUTATION_REMOVE_FROM_WAITLIST_WITH_ACTIVITY);
  const [submitRemoveWithoutActivity] = useMutation<RemoveApplicantFromList,
  RemoveApplicantFromListVariables>(MUTATION_REMOVE_FROM_WAITLIST_WITHOUT_ACTIVITY);

  const getApplicant = (inputId: string) =>
    applicants.find(({ interestId }) => interestId === inputId) ?? null;
  const setViewingById = (inputId: string | null) =>
    setViewing(inputId ? getApplicant(inputId) : null);
  const reference = buildApplicantReference(applicants);


  return <Modal
    isOpen={!!viewing}
    onRequestClose={clearViewing}
    noTitle
    hideBackdrop
  >
    {viewing && (<>
      <DialogTitle onClose={clearViewing}>
        <Typography variant="h6">
          {reference[viewing.interestId].previous && <Tooltip title="Previous Applicant">
            <IconButton
              aria-label="Previous"
              size="small" onClick={() => {
                const previousId = reference[viewing.interestId].previous;
                setViewing(getApplicant(previousId ?? ""));
              }}>
              <ArrowBack />
            </IconButton></Tooltip>}
             Manual Contact: {viewing?.name}
          {reference[viewing.interestId].next && <Tooltip title="Next Applicant">
            <IconButton
              aria-label="Next"
              size="small" onClick={() => {
                const nextId = reference[viewing.interestId].next;
                setViewing(getApplicant(nextId ?? ""));
              }}>
              <ArrowForward />
            </IconButton></Tooltip>}
        </Typography>
      </DialogTitle>

      <Formik<FormValues>
        key={viewing.interestId}
        initialValues={initialFormValues}
        onSubmit={submitFunction(
          viewing.interestId,
          nextApplicant,
          setNextApplicant,
          setViewingById,
          submitAttempt,
          submitUpdateWithActivity,
          submitUpdateWithoutActivity,
          submitRemoveWithActivity,
          submitRemoveWithoutActivity,
          openSnackbar,
        )
        }
        validationSchema={manualContactValidationSchema}
      >
        {({ values, setFieldValue }) => (<>
          <DialogContent>
            {viewing.phones.map((phone, index) =>
              <PhoneNumber
                key={`users-${phone?.number}-${index}`}
                {...phone}
                missing="No available phone number."
              />)}
            {viewing.phones.length === 0 && <PhoneNumber missing="No available phone number" number={null} />}
            <Form style={{ paddingTop: "3em" }} id={`contact-${viewing.interestId}`}>

              <div className={styles.section}>
                <SelectWithLabel
                  margin="dense"
                  name="disposition"
                  title="Log a Call"
                  style={{ backgroundColor: "white" }}
                  options={DISPOSITIONS.map(({ name, value }) =>
                    ({ label: name, value }))}

                  onBlur={(e) => {
                    if (e.target.value === DISPOSITION_MESSAGE[Disposition.NoLog]) {
                      setFieldValue("notes", "", true);
                    }
                  }}
                />
              </div>

              <div className={styles.section}>
                <Input label="Call notes" disabled={values.disposition === DISPOSITION_MESSAGE[Disposition.NoLog]} name="notes" multiline minRows={2} />
              </div>

              <div className={styles.section}>
                <div className={styles.sectionHeading}>
                            Update or remove from the waitlist?
                </div>
                <div className={styles.results}>
                  <RadioCardGroup
                    name="result"
                    options={[
                      { label: "Update", value: "update" },
                      { label: "Remove", value: "remove" },
                    ]}
                  />
                </div>
              </div>

              {values.result === "update" && (<UpdateFormFields values={values} />)}

              {values.result === "remove" && (
                <div className={styles.section}>
                  <FormikStructuredReasons
                    name="removalReason"
                    label="Removal Reason*" />
                </div>
              )}

            </Form>
          </DialogContent>
          <DialogActions>
            <Button color="secondary" size="L" onClick={(event) => {
              event.preventDefault();
              setViewing(null);
            }}>
                  Cancel
            </Button>

            {reference[viewing.interestId].next ? (
              <DropDownButton
                form={`contact-${viewing.interestId}`}
                type="submit"
                size="L"
                color="primary"
                disabled={
                  values.disposition === DISPOSITION_MESSAGE[Disposition.NoLog] &&
                      values.result === null
                }
                currentOption={submitBtnIndex}
                onOptionClick={(i) => setSubmitBtnIndex(i)}
                onButtonClick={(index) => {
                  if (index === 0) setNextApplicant(reference[viewing.interestId].next || null);
                }}
                options={["Save and continue", "Save and close"]}
              />
            ) : (
              <Button disabled={
                values.disposition === DISPOSITION_MESSAGE[Disposition.NoLog] &&
                      values.result === null
              } type="submit" color="primary" size="L" form={`contact-${viewing.interestId}`}>
                      Save and close
              </Button>
            )}
          </DialogActions>
        </>
        )}
      </Formik>
    </>)}
  </Modal>;
};
export default ModalForm;

